import React, { useState, useEffect, useMemo } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { ChevronLeft, ChevronRight, Plus, Calendar as CalIcon } from 'lucide-react'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const typeColors = {
  practice: 'bg-blue-500',
  game: 'bg-green-500',
  tournament: 'bg-purple-500',
  meeting: 'bg-amber-500',
  social: 'bg-pink-500',
}

function formatMonth(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export default function PortalCalendar() {
  const { team, canManage } = useOutletContext()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentMonth, setCurrentMonth] = useState(new Date())

  useEffect(() => {
    if (team?.team_id) fetchEvents()
  }, [team?.team_id, currentMonth])

  const fetchEvents = async () => {
    try {
      const data = await portalRequest(`/api/events/all?team_id=${team.team_id}`)
      setEvents(Array.isArray(data) ? data : data?.events || [])
    } catch (err) {
      console.error('Error fetching events:', err)
    } finally {
      setLoading(false)
    }
  }

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()

    const days = []

    // Previous month filler
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, inMonth: false, date: new Date(year, month - 1, daysInPrevMonth - i) })
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, inMonth: true, date: new Date(year, month, d) })
    }
    // Next month filler
    const remaining = 42 - days.length
    for (let d = 1; d <= remaining; d++) {
      days.push({ day: d, inMonth: false, date: new Date(year, month + 1, d) })
    }

    return days
  }, [currentMonth])

  const eventsByDate = useMemo(() => {
    const map = {}
    events.forEach(ev => {
      if (!ev.event_date) return
      const key = new Date(ev.event_date).toDateString()
      if (!map[key]) map[key] = []
      map[key].push(ev)
    })
    return map
  }, [events])

  const today = new Date().toDateString()

  const prevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  const nextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  const goToday = () => setCurrentMonth(new Date())

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Calendar</h1>
          <p className="text-zinc-400 mt-1">Team schedule overview</p>
        </div>
        {canManage && (
          <button
            onClick={() => navigate(`/portal/team/${team.team_id}/events/new`)}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Event
          </button>
        )}
      </div>

      <div className="bg-zinc-800 rounded-2xl border border-zinc-700 overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">{formatMonth(currentMonth)}</h2>
            <button
              onClick={goToday}
              className="px-3 py-1 text-xs font-medium border border-zinc-700 text-zinc-400 rounded-lg hover:text-white hover:border-zinc-600 transition-colors"
            >
              Today
            </button>
          </div>
          <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-zinc-700">
          {DAYS.map(d => (
            <div key={d} className="text-center py-2 text-xs font-semibold text-zinc-500 uppercase">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {calendarDays.map((cell, i) => {
              const dateKey = cell.date.toDateString()
              const dayEvents = eventsByDate[dateKey] || []
              const isToday = dateKey === today

              return (
                <div
                  key={i}
                  className={`min-h-[80px] sm:min-h-[100px] p-1.5 border-b border-r border-zinc-700/50 ${
                    !cell.inMonth ? 'bg-zinc-900/30' : ''
                  }`}
                >
                  <div className={`text-xs font-medium mb-1 ${
                    isToday 
                      ? 'w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center' 
                      : cell.inMonth ? 'text-zinc-300' : 'text-zinc-600'
                  }`}>
                    {cell.day}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev, j) => (
                      <button
                        key={j}
                        onClick={() => navigate(`/portal/team/${team.team_id}/events/${ev.event_id || ev._id}`)}
                        className="w-full text-left group"
                      >
                        <div className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${typeColors[ev.event_type] || 'bg-zinc-500'}`} />
                          <span className="text-xs text-zinc-300 truncate group-hover:text-white transition-colors">
                            {ev.title}
                          </span>
                        </div>
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <p className="text-xs text-zinc-500 pl-3">+{dayEvents.length - 3} more</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 justify-center">
        {Object.entries(typeColors).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
            <span className="text-xs text-zinc-500 capitalize">{type}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
